import { useContext } from 'react';

import OrderContext from 'contexts/OrderContext';
import ProductType from 'types/enums/ProductType';
import DetailItem from 'types/models/DetailItem';
import Product from 'types/models/Product';

interface UseGuarnicionExtrasProps {
  onChangeQuantity: ((id: string, quantity: number, clothingSizeId?: number) => void) | undefined;
  onDeleteProduct: ((id: string, clothingSizeId?: number) => void) | undefined;
}

function useGuarnicionExtra({ onDeleteProduct, onChangeQuantity }: UseGuarnicionExtrasProps) {
  const { currentShoppingCart, setCurrentShoppingCart } = useContext(OrderContext);

  // al agregar guarnicion o extra desde el modal
  // actualizar en el carrito la cantidad en la guarnicion/extra y en la relacion con el producto principal
  const onAddGuarnicionExtra = (guarnicionExtra: Product, mainProduct: Product) => {
    const isExtraOrGuarnicion =
      guarnicionExtra.typeId === ProductType.Extra || guarnicionExtra.typeId === ProductType.Guarnicion;

    if (!isExtraOrGuarnicion || !currentShoppingCart) return;

    const products = currentShoppingCart.detail.map((item) => {
      if (item.product.id !== guarnicionExtra.id) return item;

      const existingRelated = item.relatedProducts?.find((rel) => rel.id === mainProduct.id);
      let updatedRelatedProducts;

      if (existingRelated) {
        updatedRelatedProducts = item.relatedProducts?.map((rel) =>
          rel.id === mainProduct.id ? { ...rel, quantity: rel.quantity + 1 } : rel,
        );
      } else {
        updatedRelatedProducts = [...(item.relatedProducts ?? []), { id: mainProduct.id, quantity: 1 }];
      }

      return {
        ...item,
        quantity: item.quantity + 1,
        relatedProducts: updatedRelatedProducts,
      };
    });

    const exists = currentShoppingCart.detail.some((item) => item.product.id === guarnicionExtra.id);

    const newDetail = {
      ...currentShoppingCart,
      detail: exists
        ? products
        : [
            ...currentShoppingCart.detail,
            {
              product: guarnicionExtra,
              quantity: 1,
              relatedProducts: [{ id: mainProduct.id, quantity: 1 }],
            },
          ],
    };

    setCurrentShoppingCart(newDetail);
  };

  // al remover producto principal, remover una guarnicion/extra relacionado
  const removeGuarnicionExtraFromMainProduct = (productId: string) => {
    if (!currentShoppingCart) return;

    const guarnicionesExtrasFromProduct = currentShoppingCart.detail.filter((item) =>
      item.relatedProducts?.some((related) => related.id === productId),
    );

    for (const guarnicionExtra of guarnicionesExtrasFromProduct) {
      const relatedProduct = guarnicionExtra.relatedProducts?.find((rel) => rel.id === productId);

      if (relatedProduct) {
        relatedProduct.quantity -= 1;

        if (relatedProduct.quantity <= 0) {
          guarnicionExtra.relatedProducts = guarnicionExtra.relatedProducts?.filter((rel) => rel.id !== productId);
        }

        const totalQuantity = guarnicionExtra.relatedProducts?.reduce((sum, rel) => sum + rel.quantity, 0) ?? 0;

        if (totalQuantity <= 0) {
          onDeleteProduct?.(guarnicionExtra.product.id);
        } else {
          onChangeQuantity?.(guarnicionExtra.product.id, totalQuantity);
        }

        setCurrentShoppingCart({ ...currentShoppingCart });
        break;
      }
    }
  };

  // al remover guarnicion/extra de card producto
  const onRemoveGuarnicionExtra = (guarnicionExtraDetail: DetailItem, mainProductId: string) => {
    if (!currentShoppingCart) return;

    if (guarnicionExtraDetail.quantity === 1) {
      onDeleteProduct?.(guarnicionExtraDetail.product.id);
    } else {
      onChangeQuantity?.(guarnicionExtraDetail.product.id, guarnicionExtraDetail.quantity - 1);

      if (guarnicionExtraDetail.relatedProducts) {
        guarnicionExtraDetail.relatedProducts = guarnicionExtraDetail.relatedProducts.map((related) =>
          related.id === mainProductId ? { ...related, quantity: related.quantity - 1 } : related,
        );

        setCurrentShoppingCart({ ...currentShoppingCart });
      }
    }
  };

  return {
    onAddGuarnicionExtra,
    removeGuarnicionExtraFromMainProduct,
    onRemoveGuarnicionExtra,
  };
}

export default useGuarnicionExtra;
